import { pinnedContracts } from '@hoodmorning/config';
import type { Address, Hex } from '@hoodmorning/contracts';
import { getAddress, keccak256, type PublicClient } from 'viem';

export type ContractPin = {
  name: string;
  address: Address | null;
  expectedCodeHash: Hex | null;
};

export type CodeVerificationItem = ContractPin & {
  observedCodeHash: Hex | null;
  valid: boolean;
  failure: 'NO_CODE' | 'MISSING_EXPECTED_ADDRESS' | 'MISSING_EXPECTED_HASH' | 'HASH_MISMATCH' | null;
};

export type CodeVerificationReport = {
  valid: boolean;
  items: CodeVerificationItem[];
  failures: string[];
};

type BytecodeClient = Pick<PublicClient, 'getBytecode'>;

export const productOneContractPins: readonly ContractPin[] = [
  {
    name: 'entryPointV07',
    address: getAddress(pinnedContracts.entryPointV07.address),
    expectedCodeHash: pinnedContracts.entryPointV07.codeHash,
  },
  {
    name: 'accountFactory',
    address: getAddress(pinnedContracts.accountFactory.address),
    expectedCodeHash: pinnedContracts.accountFactory.codeHash,
  },
  {
    name: 'smaBImplementation',
    address: getAddress(pinnedContracts.smaBImplementation.address),
    expectedCodeHash: pinnedContracts.smaBImplementation.codeHash,
  },
  {
    name: 'universalRouterV211',
    address: pinnedContracts.universalRouterV211.address === null
      ? null : getAddress(pinnedContracts.universalRouterV211.address),
    expectedCodeHash: pinnedContracts.universalRouterV211.codeHash,
  },
] as const;

export async function verifyContractCode(
  client: BytecodeClient,
  pins: readonly ContractPin[] = productOneContractPins,
): Promise<CodeVerificationReport> {
  const items = await Promise.all(
    pins.map(async (pin): Promise<CodeVerificationItem> => {
      // A deliberately unfilled release identity is unavailable, not a reason
      // to crash account-only imports, drop a required pin or query a fake address.
      if (pin.address === null) {
        return { ...pin, observedCodeHash: null, valid: false, failure: 'MISSING_EXPECTED_ADDRESS' };
      }
      const code = await client.getBytecode({ address: pin.address });
      if (code === undefined || code === '0x') {
        return { ...pin, observedCodeHash: null, valid: false, failure: 'NO_CODE' };
      }

      const observedCodeHash = keccak256(code);
      if (pin.expectedCodeHash === null) {
        return {
          ...pin,
          observedCodeHash,
          valid: false,
          failure: 'MISSING_EXPECTED_HASH',
        };
      }
      if (observedCodeHash.toLowerCase() !== pin.expectedCodeHash.toLowerCase()) {
        return { ...pin, observedCodeHash, valid: false, failure: 'HASH_MISMATCH' };
      }
      return { ...pin, observedCodeHash, valid: true, failure: null };
    }),
  );

  return {
    valid: items.every((item) => item.valid),
    items,
    failures: items
      .filter((item) => !item.valid)
      .map((item) => `${item.name}:${item.failure ?? 'UNKNOWN'}`),
  };
}
